'use client';
import { TypeAccountSettingsPageTilesFields } from '@/core/generated-types';
import React, { FC } from 'react';
import useLocale from '@/hooks/useLocale';
import { Errors, Loader } from '@/components';
import { usePageData } from '@/context/PageDataProvider';
import { TypeAccountSettingsData } from '@/pages/[locale]/account/settings';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { ERROR_KINDS } from '@/core/constants';
import {
  ContactDetailsTile,
  GiftsTile,
  ManageSubscriptionsTile,
  PasswordTile,
  PaymentMethodTile,
  FamilyProfileTile,
  ManageERCSubscriptionsTile,
} from './components';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import useSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubscriptions';
import useSubEngineSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubEngineSubscriptions';
import { filterHiddenSubscriptions } from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';
import usePaymentMethods from '@/lib/api/productCatalog/hooks/usePaymentMethods';
import useCreditSummary from '@/lib/api/productCatalog/hooks/useCreditSummary';
import usePlayKitSubscriptionProduct from '@/lib/api/productCatalog/hooks/usePlayKitSubscriptionProducts';
import CommunicationPreferencesTile from './components/CommunicationPreferencesTile';
import { useFlags } from 'launchdarkly-react-client-sdk';

const AccountSettingsPageTiles: FC<TypeAccountSettingsPageTilesFields> = ({
  familyProfileTile,
  paymentMethodTile,
  contactDetailsTile,
  passwordTile,
  manageSubscriptionTile,
  manageERCSubscriptionTile,
  viewGiftsTile,
  subscriberBadge,
  translations,
  languageSettingsActionLocaleOverride,
  communicationPreferencesTile,
}) => {
  const locale = useLocale();
  const { 'retention-enable-sms-messaging': enableSmsMessaging } = useFlags();

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const pageData = usePageData() as TypeAccountSettingsData;

  const { user } = pageData;

  const { data: initialSubscriptions, loading: subscriptionsLoading } =
    useSubscriptions({
      userUuid: user.uuid,
    });

  const {
    data: subEngineSubscriptionResponse,
    isLoading: subEngineSubscriptionsLoading,
    refetch: refetchSubEngineSubscriptions,
  } = useSubEngineSubscriptions({
    userUuid: user.uuid,
    type: 'erc',
  });

  const { data: paymentMethods, loading: paymentMethodsLoading } =
    usePaymentMethods({
      userUuid: user.uuid,
    });

  const { data: creditSummaryData, loading: creditSummaryLoading } =
    useCreditSummary();

  const {
    data: playKitSubscriptionProduct,
    loading: playKitSubscriptionProductLoading,
  } = usePlayKitSubscriptionProduct({
    language: getLanguageFromLocale(locale),
  });

  const isLoading =
    subscriptionsLoading &&
    paymentMethodsLoading &&
    playKitSubscriptionProductLoading &&
    creditSummaryLoading &&
    subEngineSubscriptionsLoading;

  const t = generateTranslationsObject(
    translations.fields,
  ) as TypeAccountSettingsPageTranslations;

  if (isLoading) {
    return <Loader loading={isLoading} text={t.loading} />;
  }

  const ercSubscriptions = subEngineSubscriptionResponse?.subscriptions || [];

  const subscriptions = initialSubscriptions
    ? filterHiddenSubscriptions(initialSubscriptions, user)
    : [];

  const primaryPaymentMethod =
    paymentMethods &&
    paymentMethods.find((paymentMethod) => paymentMethod.is_primary);

  if (!_isPageDataValid(pageData)) {
    return <Errors errors={errors} heading={heading} />;
  }

  return (
    <div className="container mx-auto pb-32 mt-6 grid grid-cols-1 gap-6 lg:max-w-[1000px] lg:grid-cols-2">
      <div className="flex flex-col gap-6">
        <FamilyProfileTile
          t={t}
          user={user}
          subscriptions={subscriptions}
          tile={familyProfileTile.fields}
          locale={locale}
          subscriberBadge={subscriberBadge.fields}
        />
        <PaymentMethodTile
          t={t}
          user={user}
          paymentMethod={primaryPaymentMethod}
          tile={paymentMethodTile.fields}
          locale={locale}
          creditSummaryData={creditSummaryData}
        />
      </div>
      <div className="flex flex-col gap-6">
        <ContactDetailsTile
          t={t}
          user={user}
          tile={contactDetailsTile.fields}
          locale={locale}
        />
        <PasswordTile
          t={t}
          user={user}
          tile={passwordTile.fields}
          locale={locale}
        />
        <ManageSubscriptionsTile
          t={t}
          user={user}
          subscriptions={subscriptions}
          playKitSubscriptionProduct={playKitSubscriptionProduct}
          tile={manageSubscriptionTile.fields}
          locale={locale}
          isLanguageSettingsActionEnabled={
            !shouldHideWithLocaleOverride(languageSettingsActionLocaleOverride)
          }
        />
        <ManageERCSubscriptionsTile
          t={t}
          subscriptions={ercSubscriptions}
          tile={manageERCSubscriptionTile.fields}
          locale={locale}
          refetchSubEngineSubscriptions={refetchSubEngineSubscriptions}
        />
        <GiftsTile
          t={t}
          user={user}
          subscriptions={subscriptions}
          tile={viewGiftsTile.fields}
          locale={locale}
        />
        {enableSmsMessaging && communicationPreferencesTile && (
          <CommunicationPreferencesTile
            t={t}
            user={user}
            tile={communicationPreferencesTile?.fields}
            locale={locale}
          />
        )}
      </div>
    </div>
  );
};

function _isPageDataValid(pageData: TypeAccountSettingsData) {
  // The user is the only data type that should be consistently available across all page states.
  // To facilitate null checks we will do this before rendering any of the page tiles so the tiles can
  // depend on this data being there. The tiles should support null checks for all other dependent data.
  return pageData?.user;
}

export default AccountSettingsPageTiles;
